/**
 * Offers, worked out on a ticket.
 *
 * Four shapes cover what an Indian restaurant actually runs: buy two get one,
 * any two of something for a flat price, a percentage off a section, and a
 * combo sold at one price. Everything else people ask for turns out to be one
 * of these with different numbers in it.
 *
 * Two rules keep them explainable at a counter, and both matter more than
 * squeezing out the largest possible discount:
 *
 *  - a plate is claimed by at most one offer, so nobody has to work out how two
 *    offers interact on the same dosa in front of a waiting guest;
 *  - offers run in the order the owner arranged them, so the same ticket always
 *    produces the same bill.
 *
 * Everything here is pure. It is given the lines, the offers and the time, and
 * hands back what comes off which line - no database, no clock of its own.
 */
import { roundHalfUp } from './money.js';
import { localDate } from './stay.js';
export const OFFER_TYPES = ['BUY_X_GET_Y', 'SET_PRICE', 'PERCENT_OFF', 'COMBO'];
/* ---------------- when an offer is running ---------------- */
function localParts(at, timeZone) {
    const format = (options) => {
        try {
            return new Intl.DateTimeFormat('en-GB', { timeZone: timeZone || 'UTC', ...options }).format(at);
        }
        catch {
            return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', ...options }).format(at);
        }
    };
    const [hour, minute] = format({ hour: '2-digit', minute: '2-digit', hour12: false })
        .split(':')
        .map(Number);
    const weekday = format({ weekday: 'short' });
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return {
        // 24:00 is midnight at the start of the day, not the end of it.
        minutes: ((hour ?? 0) % 24) * 60 + (minute ?? 0),
        day: Math.max(0, days.indexOf(weekday)),
    };
}
/**
 * Whether this offer is running at `at`, where the hotel is.
 *
 * Judged in the property's own time zone throughout: one in the morning in
 * Bengaluru is still the previous evening by UTC, and a late-night offer keyed
 * off UTC would switch itself on and off five and a half hours early.
 */
export function isOfferLive(offer, at, timeZone) {
    const today = localDate(at, timeZone);
    if (offer.startsOn && today < offer.startsOn)
        return false;
    if (offer.endsOn && today > offer.endsOn)
        return false;
    const { minutes, day } = localParts(at, timeZone);
    if (offer.daysOfWeek && offer.daysOfWeek.length > 0 && !offer.daysOfWeek.includes(day)) {
        return false;
    }
    const from = offer.startMinute;
    const to = offer.endMinute;
    if (from === null || from === undefined || to === null || to === undefined)
        return true;
    // A window that ends before it starts runs through midnight.
    return from <= to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}
const inScope = (offer, line) => {
    if (offer.categoryId)
        return line.categoryId === offer.categoryId;
    return offer.items.some((item) => item.menuItemId === line.menuItemId);
};
/** Every plate the offer may touch, dearest first or cheapest first. */
function availableUnits(units, lines, offer) {
    return units.filter((unit) => !unit.claimed && inScope(offer, lines[unit.lineIndex]));
}
const byPriceAscending = (a, b) => a.unitPrice - b.unitPrice;
const byPriceDescending = (a, b) => b.unitPrice - a.unitPrice;
export function applyOffers(input) {
    const { lines, offers, at, timeZone } = input;
    const units = [];
    lines.forEach((line, lineIndex) => {
        for (let n = 0; n < Math.floor(line.quantity); n += 1) {
            units.push({ lineIndex, unitPrice: line.unitPrice, claimed: false });
        }
    });
    const perLine = lines.map(() => 0);
    const applied = [];
    const ticketValue = lines.reduce((sum, l) => sum + l.unitPrice * Math.floor(l.quantity), 0);
    const live = offers
        .filter((offer) => isOfferLive(offer, at, timeZone))
        .filter((offer) => ticketValue >= (offer.minTicket ?? 0))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
    for (const offer of live) {
        const cap = offer.maxPerTicket && offer.maxPerTicket > 0 ? offer.maxPerTicket : Infinity;
        const take = (unit, amount) => {
            unit.claimed = true;
            const capped = Math.min(amount, unit.unitPrice);
            perLine[unit.lineIndex] = (perLine[unit.lineIndex] ?? 0) + capped;
            return capped;
        };
        let discount = 0;
        let times = 0;
        if (offer.type === 'BUY_X_GET_Y') {
            const buy = offer.buyQty ?? 0;
            const free = offer.freeQty ?? 0;
            if (buy > 0 && free > 0) {
                // Cheapest go free: nobody runs "buy two, get the dearest one free".
                let pool = availableUnits(units, lines, offer).sort(byPriceAscending);
                while (pool.length >= buy + free && times < cap) {
                    const freed = pool.slice(0, free);
                    const paidFor = pool.slice(free, free + buy);
                    for (const unit of freed)
                        discount += take(unit, unit.unitPrice);
                    for (const unit of paidFor)
                        unit.claimed = true;
                    times += 1;
                    pool = availableUnits(units, lines, offer).sort(byPriceAscending);
                }
            }
        }
        else if (offer.type === 'SET_PRICE') {
            const size = offer.setQty ?? 0;
            const price = offer.setPrice ?? 0;
            if (size > 0) {
                // Dearest first, which is what "any two for 99" is understood to mean.
                let pool = availableUnits(units, lines, offer).sort(byPriceDescending);
                while (pool.length >= size && times < cap) {
                    const set = pool.slice(0, size);
                    const worth = set.reduce((sum, unit) => sum + unit.unitPrice, 0);
                    // Two cheap items for a flat price is a surcharge, not an offer.
                    if (worth <= price)
                        break;
                    let owed = worth - price;
                    for (const unit of set) {
                        const share = Math.min(owed, unit.unitPrice);
                        discount += take(unit, share);
                        owed -= share;
                    }
                    times += 1;
                    pool = availableUnits(units, lines, offer).sort(byPriceDescending);
                }
            }
        }
        else if (offer.type === 'PERCENT_OFF') {
            const percent = Math.min(offer.percentOff ?? 0, 100);
            if (percent > 0) {
                for (const unit of availableUnits(units, lines, offer)) {
                    if (times >= cap)
                        break;
                    discount += take(unit, roundHalfUp((unit.unitPrice * percent) / 100));
                    times += 1;
                }
            }
        }
        else if (offer.type === 'COMBO') {
            const price = offer.comboPrice ?? 0;
            const members = offer.items.filter((member) => member.quantity > 0);
            if (members.length > 0) {
                for (;;) {
                    if (times >= cap)
                        break;
                    // One of each member, cheapest first so the guest keeps the dearest.
                    const set = [];
                    let complete = true;
                    for (const member of members) {
                        const pool = units
                            .filter((unit) => !unit.claimed &&
                            !set.includes(unit) &&
                            lines[unit.lineIndex].menuItemId === member.menuItemId)
                            .sort(byPriceAscending);
                        if (pool.length < member.quantity) {
                            complete = false;
                            break;
                        }
                        set.push(...pool.slice(0, member.quantity));
                    }
                    if (!complete)
                        break;
                    const worth = set.reduce((sum, unit) => sum + unit.unitPrice, 0);
                    if (worth <= price)
                        break;
                    let owed = worth - price;
                    for (const unit of set) {
                        const share = Math.min(owed, unit.unitPrice);
                        discount += take(unit, share);
                        owed -= share;
                    }
                    times += 1;
                }
            }
        }
        // An offer that gave nothing away does not belong on the bill.
        if (discount > 0) {
            applied.push({ offerId: offer.id, name: offer.name, type: offer.type, discount, times });
        }
    }
    return { applied, perLine, total: perLine.reduce((sum, value) => sum + value, 0) };
}
//# sourceMappingURL=offers.js.map